import { useEffect, useState } from "react";
import { CalendarCheck, Star, Award, CheckCircle2, CircleDashed, MonitorPlay, Users } from "lucide-react";
import { ClassStarActions } from "@/components/ClassStar";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";
import FREE from "@/lib/classroom_free.json";

const BY = Object.fromEntries(FREE.map((l) => [l.slug, l]));
const short = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
const Stars = ({ n, testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid} aria-label={`${n}/5`}>
    {[0, 1, 2, 3, 4].map((k) => <Star key={k} className={`w-4 h-4 ${k < n ? "fill-[#FFC94D] text-[#B45309]" : "text-slate-300"}`} />)}
    <b className="ml-1 text-xs text-[#0A192F]">{n}/5</b>
  </span>
);
const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl bg-[#F0F4F8] border border-slate-200 p-4" data-testid={testid}>
    <Icon className="w-5 h-5 text-[#FF2E63]" />
    <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-2xl font-extrabold text-[#0A192F]">{value}</div>
  </div>
);

// Virtual-class history for one student: attendance, stars per lesson and downloadable Class Star badges.
export const ClassHistory = ({ h, name, testid = "class-history" }) => {
  const { L } = useLang();
  const lessons = Object.fromEntries((h.lessons || []).map((x) => [x.slug, x]));
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-3 gap-3">
        <Tile icon={CalendarCheck} label={L("Classes attended", "अटेंड की क्लास")} value={h.classes_attended} testid={`${testid}-attended`} />
        <Tile icon={Star} label={L("Stars", "स्टार")} value={`${h.stars}/${FREE.length * 5}`} testid={`${testid}-stars`} />
        <Tile icon={Award} label={L("Class Stars", "क्लास स्टार")} value={h.badges} testid={`${testid}-badges`} />
      </div>
      <ul className="mt-4 divide-y divide-slate-100">
        {FREE.map((l) => {
          const r = lessons[l.slug];
          const dates = (h.attendance || []).filter((a) => a.slug === l.slug).map((a) => short(a.marked_at));
          return (
            <li key={l.slug} className="py-3 flex flex-wrap items-center gap-3" data-testid={`${testid}-lesson-${l.n}`}>
              <img src={l.poster} alt="" loading="lazy" className="w-20 aspect-video rounded-lg object-cover bg-[#D6E6FA]" />
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-bold text-[#0A192F] line-clamp-1">{L(`Lesson ${l.n} · ${l.title}`, `पाठ ${l.n} · ${l.title_hi}`)}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                  {dates.length ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{L("Present:", "उपस्थित:")} {dates.slice(0, 4).join(", ")}{dates.length > 4 ? ` +${dates.length - 4}` : ""}</>
                    : <><CircleDashed className="w-3.5 h-3.5" />{L("Not attended yet", "अभी अटेंड नहीं की")}</>}
                </div>
              </div>
              <Stars n={r?.stars || 0} testid={`${testid}-lesson-${l.n}-stars`} />
              {r?.badge_no && <ClassStarActions lesson={l} name={name} badgeNo={r.badge_no} at={r.badge_at} testid={`${testid}-classstar-${l.n}`} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const StudentClassHistory = () => {
  const { L } = useLang();
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/student/classroom/history", authHeader("student")).then((r) => setH(r.data)).catch(() => setH(false)); }, []);
  if (h === false) return null;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="student-class-history">
      <h2 className="text-xl font-extrabold text-[#0A192F] flex items-center gap-2"><MonitorPlay className="w-5 h-5 text-[#FF2E63]" />{L("My virtual classes", "मेरी वर्चुअल क्लास")}</h2>
      <p className="text-sm text-slate-500">{L("Attendance, stars and Class Star badges from Zoya Ma'am's classes.", "ज़ोया मैम की क्लास की attendance, स्टार और क्लास स्टार बैज।")}</p>
      <div className="mt-4">{h ? <ClassHistory h={h} name={h.name} testid="student-history" /> : <div className="h-24 rounded-2xl bg-slate-900/5 animate-pulse" />}</div>
    </div>
  );
};

export const ParentClassHistory = ({ creds }) => {
  const { L } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.post("/progress/virtual-class", creds).then((r) => setD(r.data)).catch(() => setD({ children: [] })); }, [creds]);
  if (!d) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="parent-class-history">
      <h3 className="font-bold text-[#0A192F] flex items-center gap-2"><MonitorPlay className="w-5 h-5 text-[#FF2E63]" />{L("Virtual classes with Zoya Ma'am", "ज़ोया मैम की वर्चुअल क्लास")}</h3>
      {d.children.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="parent-class-history-empty">{L("No approved RoboSpark student account is linked to this phone or email yet. Your child can sign up at /student/login using your phone number as the parent phone.", "इस फ़ोन या ईमेल से कोई मंज़ूर छात्र अकाउंट जुड़ा नहीं है। बच्चा /student/login पर साइन अप करके आपका फ़ोन नंबर अभिभावक फ़ोन में डाल सकता है।")}</p>
      ) : d.children.map((c, i) => (
        <div key={i} className="mt-4">
          <div className="text-sm font-bold text-[#0A192F] mb-2" data-testid={`parent-child-${i}`}>{c.student_name} · {L(`Class ${c.class_grade}`, `कक्षा ${c.class_grade}`)} · {c.school_name}</div>
          <ClassHistory h={c} name={c.student_name} testid={`parent-history-${i}`} />
        </div>
      ))}
    </div>
  );
};

export const TrainerClassReport = ({ auth }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/teacher/virtual-class", auth).then((r) => setD(r.data)).catch(() => setD({ items: [] })); }, [auth]);
  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200" data-testid="trainer-class-report">
      <div className="p-5 border-b border-slate-200">
        <h2 className="font-bold text-lg text-[#0A192F] flex items-center gap-2"><Users className="w-5 h-5 text-[#FF2E63]" />Virtual classroom · attendance & stars</h2>
        <p className="text-xs text-slate-500">Students of your school with an approved RoboSpark student account</p>
      </div>
      {!d ? <div className="m-5 h-16 rounded-2xl bg-slate-900/5 animate-pulse" /> : d.items.length === 0 ? (
        <p className="p-8 text-center text-slate-500" data-testid="trainer-class-report-empty">No approved student accounts for your school yet.</p>
      ) : (
        <ul className="divide-y" data-testid="trainer-class-report-list">
          {d.items.map((s) => (
            <li key={s.student_code || s.student_name} className="p-4 flex flex-wrap items-center gap-4" data-testid={`trainer-class-row-${s.student_code}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-[#0A192F]">{s.student_name}</div>
                <div className="text-xs text-slate-500">Class {s.class_grade} · Roll {s.roll_no || "—"} · {s.last_attended ? `last class ${short(s.last_attended)}` : "no class yet"}</div>
              </div>
              <div className="text-xs text-slate-600 flex gap-4">
                <span data-testid={`trainer-class-attended-${s.student_code}`}><b className="text-[#0A192F] text-sm">{s.classes_attended}</b> classes</span>
                <span data-testid={`trainer-class-stars-${s.student_code}`}><Star className="inline w-3.5 h-3.5 fill-[#FFC94D] text-[#B45309] -mt-0.5" /> <b className="text-[#0A192F] text-sm">{s.stars}</b></span>
                <span data-testid={`trainer-class-badges-${s.student_code}`}><Award className="inline w-3.5 h-3.5 text-[#FF2E63] -mt-0.5" /> <b className="text-[#0A192F] text-sm">{s.badges}</b> Class Stars</span>
              </div>
              <div className="w-full flex flex-wrap gap-1.5">
                {FREE.map((l) => {
                  const r = (s.lessons || []).find((x) => x.slug === l.slug);
                  return <span key={l.slug} title={BY[l.slug].title} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${r?.present ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "border-slate-200 text-slate-400"}`}>L{l.n} · {r?.stars || 0}★</span>;
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
